import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { IntlMessageFormat } from 'intl-messageformat';
import { LOCALES, createTranslator, errorMessageKey } from '@kidova/i18n';
import type { AppLocale } from '../lib/preferences';
import type enCatalog from './en.json';

/**
 * Dashboard copy lives in this app's ICU catalogs (ADR 0602); component copy stays in @kidova/i18n.
 * English is the source; a missing key falls back to English (when loaded) with English plural
 * rules, else to the key. catalogs.test.ts keeps every language complete.
 */
export type MessageKey = Extract<keyof typeof enCatalog, string>;
export type MessageArgs = Record<string, string | number | Date>;

/** Each language arrives through loadLocale() before anything renders in it (English too). */
export const CATALOGS: Record<AppLocale, Partial<Record<MessageKey, string>>> = {
  en: {},
  de: {},
  ar: {},
};

export interface AppI18n {
  locale: AppLocale;
  dir: 'ltr' | 'rtl';
  intl: string;
  t: (key: MessageKey, args?: MessageArgs) => string;
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  date: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) => string;
  time: (value: Date | string | number) => string;
  relative: (value: Date | string | number, now?: number) => string;
  /** Amount in ISO 4217 minor units (§19.8.4), with the currency's own exponent. */
  money: (minor: number, currency: string) => string;
  /** The shared catalog's text for an API error code (@kidova/i18n errorMessageKey). */
  errorCode: (code: string) => string;
}

export function createI18n(locale: AppLocale): AppI18n {
  const intl = LOCALES[locale].intl;
  const cache = new Map<string, IntlMessageFormat>();
  const format = (key: MessageKey): IntlMessageFormat => {
    const own = CATALOGS[locale][key];
    const cacheKey = `${own === undefined ? 'en' : locale}:${key}`;
    let compiled = cache.get(cacheKey);
    if (!compiled) {
      compiled = new IntlMessageFormat(
        own ?? CATALOGS.en[key] ?? key,
        own === undefined ? 'en' : intl,
      );
      cache.set(cacheKey, compiled);
    }
    return compiled;
  };
  const toDate = (v: Date | string | number) => (v instanceof Date ? v : new Date(v));
  const rtf = new Intl.RelativeTimeFormat(intl, { numeric: 'auto' });
  const shared = createTranslator(locale);
  return {
    locale,
    errorCode: (code) => shared.t(errorMessageKey(code)),
    dir: locale === 'ar' ? 'rtl' : 'ltr',
    intl,
    t: (key, args) => String(format(key).format(args)),
    number: (value, options) => new Intl.NumberFormat(intl, options).format(value),
    date: (value, options = { dateStyle: 'medium' }) =>
      new Intl.DateTimeFormat(intl, options).format(toDate(value)),
    time: (value) =>
      new Intl.DateTimeFormat(intl, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).format(toDate(value)),
    relative: (value, now = Date.now()) => {
      const seconds = Math.round((toDate(value).getTime() - now) / 1000);
      const abs = Math.abs(seconds);
      if (abs < 45) return rtf.format(0, 'second');
      if (abs < 2700) return rtf.format(Math.round(seconds / 60), 'minute');
      if (abs < 64_800) return rtf.format(Math.round(seconds / 3600), 'hour');
      return rtf.format(Math.round(seconds / 86_400), 'day');
    },
    money: (minor, currency) => {
      const nf = new Intl.NumberFormat(intl, { style: 'currency', currency });
      const digits = nf.resolvedOptions().maximumFractionDigits ?? 2;
      return nf.format(minor / 10 ** digits);
    },
  };
}

const I18nContext = createContext<AppI18n | null>(null);

export function I18nProvider({ locale, children }: { locale: AppLocale; children: ReactNode }) {
  const value = useMemo(() => createI18n(locale), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): AppI18n {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n outside I18nProvider');
  return value;
}
